import { Mark, mergeAttributes } from "@tiptap/core";

// Mirrors FontSize (./font-size.ts): legacy <font color="#rrggbb">, not a CSS-based color
// extension, so it composes with FontSize on the same <font> tag (nested <font size><font
// color>text</font></font> is valid HTML) instead of fighting it over the element.
//
// Reading is wider than writing. Nothing outside this editor writes <font color> any more:
// Word, Google Docs, web pages and Quizora all colour text with CSS, so a paste from any of
// them used to arrive with the colour silently dropped. Those are parsed too and normalised
// into the stored legacy shape, which keeps the storage format exactly as it was.

/** The legacy color attribute wants a hex value; getComputedStyle-style rgb() is not valid there. */
function toHex(value: string): string | null {
  const raw = value.trim();
  if (!raw || /^(inherit|initial|unset|currentcolor|transparent)$/i.test(raw)) return null;

  const rgb = /^rgba?\(\s*([0-9.]+)[\s,]+([0-9.]+)[\s,]+([0-9.]+)/i.exec(raw);
  if (rgb) {
    const hex = [rgb[1], rgb[2], rgb[3]]
      .map((n) => Math.max(0, Math.min(255, Math.round(Number(n)))).toString(16).padStart(2, "0"))
      .join("");
    return "#" + hex;
  }

  // #rgb, #rrggbb, or a named colour, all of which the attribute accepts as-is.
  if (/^#[0-9a-f]{3,8}$/i.test(raw) || /^[a-z]+$/i.test(raw)) return raw;
  return null;
}

/** Black is what text already is, so tagging every pasted run as explicitly black is noise. */
function isDefaultBlack(hex: string): boolean {
  return /^#(000|000000|000000ff)$/i.test(hex) || /^black$/i.test(hex);
}

export const FontColor = Mark.create({
  name: "fontColor",

  addAttributes() {
    return {
      color: {
        default: null,
        // Tag rules hand over the element. Style rules hand over the declaration's value
        // instead, and supply the attribute themselves in getAttrs below, so this only has
        // to cope with the element case.
        parseHTML: (el: HTMLElement) => el?.getAttribute?.("color") ?? null,
        renderHTML: (attrs: { color: string }) => (attrs.color ? { color: attrs.color } : {}),
      },
    };
  },

  parseHTML() {
    return [
      { tag: "font[color]" },
      {
        // Any element may carry the colour, not just a span: Word puts it on paragraphs and
        // Docs on nested spans. Returning false rejects the rule so nothing else is affected.
        style: "color",
        getAttrs: (value: string) => {
          const hex = toHex(String(value));
          return hex && !isDefaultBlack(hex) ? { color: hex } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["font", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setFontColor:
        (color: string) =>
        ({ commands }: any) =>
          commands.setMark(this.name, { color }),
      unsetFontColor:
        () =>
        ({ commands }: any) =>
          commands.unsetMark(this.name),
    } as any;
  },
});
